/**
 * Which lane an inbox record belongs in, the same on every surface (the workbench, the console,
 * the phone): **inbox** is what a person should look at (approvals, decisions, questions, what
 * waits on them, what went wrong), **log** is news (turns, routines, reviews, placements, toasts),
 * grouped by day where it is shown at all. The rule is the record's `dedupeKey` prefix first,
 * then its attention. AIOE's own contract (the workbench's `shared/protocol/inbox.ts`
 * classification moved here); the workbench copies it into `shared/schemas/inboxLanes.ts`.
 *
 * Words for people: Inbox, What happened; For you (`attentionFor`), Mine (`addressedTo`).
 */
/** Prefixes (up to and including the colon) that are always the Inbox lane. */
export const INBOX_PREFIXES = ["approval:", "landing:", "integration-conflict:", "acceptance:", "label-held:", "connector.sign-in:", "decision:", "wake:", "agent.crashed:", "pr:", "risk-review:", "secret-stale:", "signin:", "order:", "work-claim:", "promotion:", "budget:", "incident:", "team-proposal:", "memory-proposal:", "app-review:", "routine-review:"];
/** Keys that are the Inbox lane exactly, not as a prefix: the desktop's `update` (an update waits) is, its `update-cleanup` (fyi) is not. */
export const INBOX_KEYS = ["update"];
/** Prefixes that are always the log lane, whatever the attention. */
export const LOG_PREFIXES = ["turn:", "routine:", "team.denied:", "team.placement", "team.member.dispatch:", "presence:", "notify:", "cloud-task:", "cloud-agents:", "memory-reviewed:"];
/** A pull request's `pr:<repo>:<n>:author` record is news to its author, not an item to act on. */
const isAuthorPr = (key) => key.startsWith("pr:") && key.endsWith(":author");
/**
 * The lane for a record: by `dedupeKey` prefix, then by attention. `policy:` is Inbox only when
 * it needs the person; `project-task:` only when the task is blocked; a task's `:review` is log;
 * anything else is Inbox when it needs the person or asks a question, log otherwise.
 */
export function inboxLane(n) {
    const key = n.dedupeKey ?? "";
    if (isAuthorPr(key))
        return "log";
    if (key.startsWith("task:") && key.endsWith(":review"))
        return "log";
    if (LOG_PREFIXES.some((p) => key.startsWith(p)))
        return "log";
    if (key.startsWith("policy:"))
        return n.attention === "needs-you" ? "inbox" : "log";
    if (key.startsWith("project-task:"))
        return /blocked/i.test(n.title) || n.attention === "needs-you" ? "inbox" : "log";
    if (INBOX_KEYS.includes(key) || INBOX_PREFIXES.some((p) => key.startsWith(p)))
        return "inbox";
    return n.attention === "needs-you" || n.kind === "question" ? "inbox" : "log";
}
/** The records of one lane. */
export function inboxLaneOf(items, lane) {
    return items.filter((n) => inboxLane(n) === lane);
}
/**
 * Whether a record is for this person in particular: its `to` names them, by subject id, or names
 * a group or role. A surface that does not know the person's groups and roles counts a group or
 * role target as theirs (the inbox reads serve a member only the records addressed to them or to
 * nobody; a project's owners also read those addressed to others, by person). A record addressed
 * to nobody in particular is the project's, whatever its attention says.
 */
export function addressedTo(n, me) {
    return Boolean(n.to?.some((t) => {
        if (t.subjectType === "user")
            return t.subjectId === me.id;
        const held = t.subjectType === "group" ? me.groups : me.roles;
        return held === undefined || held.includes(t.subjectId);
    }));
}
/**
 * The attention a row shows this person, the same on every surface: "For you" only on a record
 * addressed to them (`addressedTo`); a `for-you` record addressed to nobody in particular, or to
 * someone else, reads as `fyi`. Needs-you is left as it is: an approval for the project waits on
 * whoever answers it.
 */
export function attentionFor(n, me) {
    return n.attention === "for-you" && !addressedTo(n, me) ? "fyi" : n.attention;
}
/**
 * One row per task change for this person. A change to a task that is theirs comes twice (the
 * project's news and their own record, `task:…:for:<them>`): only theirs is shown. A record
 * addressed to someone else about their task (an owner reads those on the project page) is left
 * out: the project's news says the same.
 */
export function oneRecordPerTask(items, me) {
    const own = (n) => Boolean(n.to?.length && n.dedupeKey?.startsWith("task:") && n.dedupeKey.includes(":for:"));
    const kept = items.filter((n) => !own(n) || addressedTo(n, { id: me }));
    const theirs = new Set(kept.filter(own).map((n) => `${n.projectId}/${n.dedupeKey.slice(0, n.dedupeKey.lastIndexOf(":for:"))}`));
    return theirs.size ? kept.filter((n) => own(n) || !n.dedupeKey || !theirs.has(`${n.projectId}/${n.dedupeKey}`)) : kept;
}
//# sourceMappingURL=inboxLanes.js.map